const STATE_KEY = '__hs_granite_health__';
const PATCHED_KEY = '__hs_granite_health_patched__';

export interface HealthSnapshot {
  listenerCount: number;
  timerCount: number;
  observerCount: number;
}

export interface AppDeltaEntry extends HealthSnapshot {
  appName: string;
}

interface GraniteHealthState {
  listenerCount: number;
  timerCount: number;
  observerCount: number;
  transitionCount: number;
  activeTimerIds: Set<number>;
  previousSnapshot: HealthSnapshot | null;
  appDeltas: AppDeltaEntry[];
  thresholdFired: boolean;
}

function getState(): GraniteHealthState {
  const win = window as any;
  if (!win[STATE_KEY]) {
    win[STATE_KEY] = {
      listenerCount: 0,
      timerCount: 0,
      observerCount: 0,
      transitionCount: 0,
      activeTimerIds: new Set<number>(),
      previousSnapshot: null,
      appDeltas: [],
      thresholdFired: false,
    };
  }
  return win[STATE_KEY];
}

export interface GraniteHealthCounts {
  listenerCount: number;
  timerCount: number;
  observerCount: number;
  transitionCount: number;
}

export function getCounts(): GraniteHealthCounts {
  const { listenerCount, timerCount, observerCount, transitionCount } =
    getState();
  return { listenerCount, timerCount, observerCount, transitionCount };
}

export function incrementTransitionCount(): void {
  getState().transitionCount++;
}

export function getPreviousSnapshot(): HealthSnapshot | null {
  return getState().previousSnapshot ?? null;
}

export function saveSnapshot(snapshot: HealthSnapshot): void {
  getState().previousSnapshot = snapshot;
}

export function accumulateDelta(appName: string, delta: HealthSnapshot): void {
  const state = getState();
  if (!state.appDeltas) {
    state.appDeltas = [];
  }
  state.appDeltas.push({ appName, ...delta });
}

export function getAllDeltas(): AppDeltaEntry[] {
  return getState().appDeltas ?? [];
}

export function hasThresholdFired(): boolean {
  return getState().thresholdFired ?? false;
}

export function markThresholdFired(): void {
  getState().thresholdFired = true;
}

export function installGraniteHealthPatches(): void {
  const win = window as any;
  if (win[PATCHED_KEY]) {
    return;
  }
  win[PATCHED_KEY] = true;
  patchEventListeners();
  patchTimers();
  patchObserver('MutationObserver');
  patchObserver('IntersectionObserver');
  patchObserver('ResizeObserver');
  patchObserver('PerformanceObserver');
}

function patchEventListeners(): void {
  const origAdd = EventTarget.prototype.addEventListener;
  const origRemove = EventTarget.prototype.removeEventListener;

  // listenerCount is a best-effort approximation. The browser silently
  // deduplicates same-triple addEventListener calls and silently no-ops
  // removeEventListener for unregistered listeners; we can't detect either
  // without a per-target registry.
  EventTarget.prototype.addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ): void {
    getState().listenerCount++;
    return origAdd.call(this, type, listener, options);
  };

  EventTarget.prototype.removeEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions
  ): void {
    const state = getState();
    if (state.listenerCount > 0) {
      state.listenerCount--;
    }
    return origRemove.call(this, type, listener, options);
  };
}

function patchTimers(): void {
  const origSetInterval = window.setInterval.bind(window);
  const origClearInterval = window.clearInterval.bind(window);
  const origSetTimeout = window.setTimeout.bind(window);
  const origClearTimeout = window.clearTimeout.bind(window);

  window.setInterval = function (
    handler: TimerHandler,
    timeout?: number,
    ...args: any[]
  ): number {
    const id = origSetInterval(handler, timeout, ...args);
    const state = getState();
    state.timerCount++;
    state.activeTimerIds.add(id);
    return id;
  };

  window.clearInterval = function (id?: number): void {
    if (id !== undefined) {
      const state = getState();
      if (state.activeTimerIds.has(id)) {
        state.activeTimerIds.delete(id);
        state.timerCount--;
      }
    }
    origClearInterval(id);
  };

  window.setTimeout = function (
    handler: TimerHandler,
    timeout?: number,
    ...args: any[]
  ): number {
    // String handlers can't be wrapped to decrement on fire; skip counting them.
    if (typeof handler !== 'function') {
      return origSetTimeout(handler, timeout, ...args);
    }
    const idRef: { current: number } = { current: 0 };
    const wrappedHandler = (...cbArgs: any[]) => {
      const state = getState();
      if (state.activeTimerIds.has(idRef.current)) {
        state.activeTimerIds.delete(idRef.current);
        state.timerCount--;
      }
      (handler as Function)(...cbArgs);
    };
    idRef.current = origSetTimeout(wrappedHandler, timeout, ...args);
    const state = getState();
    state.timerCount++;
    state.activeTimerIds.add(idRef.current);
    return idRef.current;
  };

  window.clearTimeout = function (id?: number): void {
    if (id !== undefined) {
      const state = getState();
      if (state.activeTimerIds.has(id)) {
        state.activeTimerIds.delete(id);
        state.timerCount--;
      }
    }
    origClearTimeout(id);
  };
}

function patchObserver(
  name:
    | 'MutationObserver'
    | 'IntersectionObserver'
    | 'ResizeObserver'
    | 'PerformanceObserver'
): void {
  const Original = window[name] as (new (...args: any[]) => any) | undefined;
  if (!Original) {
    return;
  }

  const Patched = class extends Original {
    constructor(...args: any[]) {
      super(...args);
      getState().observerCount++;
    }

    disconnect(): void {
      const state = getState();
      if (state.observerCount > 0) {
        state.observerCount--;
      }
      super.disconnect();
    }
  };

  Object.defineProperty(Patched, 'name', { value: name });
  (window as any)[name] = Patched;
}
